import { formatISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { gql, useClient } from 'urql';

import { RangeDate, RangeInt } from '@/globalTypes';
import { useQueryParamsWithTime, useRgVersion } from '@/hooks';
import { downloadFile, mapVariables } from '@/utils';
import { RgRiskAssessmentReportQuery } from './__generated__/useRgRiskAssessmentReport';
import { queryParams } from './queryParams';

const RG_RISK_ASSESSMENT_REPORT = gql`
  query RgRiskAssessmentReport(
    $playerGlobalId: ID
    $percentage: RangeInt
    $rgCraRiskLevels: [RgCraV2RiskLevel!]
    $rgCraReviewStatus: [RgCraV2ReviewStatus!]
    $calculatedAt: RangeDate
    $lastReviewedAt: RangeDate
    $orderBy: RgCraV2RiskAggregationOrderBy
    $desc: Boolean
    $rgCraRiskTerms: [RgCraV2RiskTerm!]
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    viewer {
      id
      rgCraRiskAssessmentsReport: rgCraV2RiskAssessmentsReport(
        playerGlobalId: $playerGlobalId
        percentage: $percentage
        rgCraRiskLevels: $rgCraRiskLevels
        rgCraReviewStatus: $rgCraReviewStatus
        calculatedAt: $calculatedAt
        lastReviewedAt: $lastReviewedAt
        orderBy: $orderBy
        desc: $desc
        rgCraRiskTerms: $rgCraRiskTerms
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        url
      }
    }
  }
`;

const LEGACY_RG_RISK_ASSESSMENT_REPORT = gql`
  query RgRiskAssessmentReportV1(
    $playerGlobalId: ID
    $rgCraRiskLevels: [RgCraRiskLevel!]
    $rgCraReviewStatus: [RgCraReviewStatus!]
    $calculatedAt: RangeDate
    $lastReviewedAt: RangeDate
    $orderBy: RgCraRiskAggregationOrderBy
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    viewer {
      id
      rgCraRiskAssessmentsReport: rgCraRiskAssessmentsReport(
        playerGlobalId: $playerGlobalId
        rgCraRiskLevels: $rgCraRiskLevels
        rgCraReviewStatus: $rgCraReviewStatus
        calculatedAt: $calculatedAt
        lastReviewedAt: $lastReviewedAt
        orderBy: $orderBy
        desc: $desc
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        url
      }
    }
  }
`;

export default function useRgRiskAssessmentReport() {
  const { isLegacy } = useRgVersion();
  const [query] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });

  const [fetching, setFetching] = useState(false);

  const calculatedAt = useMemo((): RangeDate | undefined => {
    if (query.from && query.to) {
      return {
        from: formatISO(query.from),
        to: formatISO(query.to),
      };
    }

    return undefined;
  }, [query.from, query.to]);

  const lastReviewedAt = useMemo((): RangeDate | undefined => {
    if (query.lastReviewedFrom && query.lastReviewedTo) {
      return {
        from: formatISO(query.lastReviewedFrom),
        to: formatISO(query.lastReviewedTo),
      };
    }

    return undefined;
  }, [query.lastReviewedFrom, query.lastReviewedTo]);

  const percentage = useMemo((): RangeInt | undefined => {
    if (query.percentageFrom || query.percentageTo) {
      return {
        from: query.percentageFrom ?? 0,
        to: query.percentageTo ?? 100,
      };
    }

    return undefined;
  }, [query.percentageFrom, query.percentageTo]);

  const rgCraRiskTerms = useMemo(() => {
    const riskId = query.rgCraRiskTermRiskId;

    if (riskId && query.rgCraRiskTermRiskLevels?.length) {
      return query.rgCraRiskTermRiskLevels.map((riskLevel) => ({
        riskId,
        riskLevel,
      }));
    }

    return undefined;
  }, [query.rgCraRiskTermRiskId, query.rgCraRiskTermRiskLevels]);

  const {
    percentageFrom: _percentageFrom,
    percentageTo: _percentageTo,
    rgCraRiskTermRiskId: _rgCraRiskTermRiskId,
    rgCraRiskTermRiskLevels: _rgCraRiskTermRiskLevels,
    ...queryVariables
  } = query;

  const client = useClient();

  const generate = async () => {
    setFetching(true);

    const reportVariables = isLegacy
      ? {
          ...mapVariables(queryVariables),
          calculatedAt,
          lastReviewedAt,
        }
      : {
          ...mapVariables(queryVariables),
          calculatedAt,
          lastReviewedAt,
          percentage,
          rgCraRiskTerms,
        };

    return client
      .query<RgRiskAssessmentReportQuery>(
        isLegacy ? LEGACY_RG_RISK_ASSESSMENT_REPORT : RG_RISK_ASSESSMENT_REPORT,
        reportVariables,
      )
      .toPromise()
      .then(async (res) => {
        const url = res.data?.viewer.rgCraRiskAssessmentsReport.url;

        if (url) {
          await downloadFile(url, {
            fileName: 'rg-cra-risk-assessments-report.csv',
          });
        }

        setFetching(false);
      });
  };

  return {
    generate,
    fetching,
  };
}
